import type { ChatMessage } from './index.ts';

/** One user↔assistant turn on an active path (asides for that turnId included). */
export interface TurnStep {
  user: ChatMessage;
  assistant: ChatMessage | null;
  asides: ChatMessage[];
  /** Last message id in this step (assistant, else last aside, else user) — used as leaf when stepping. */
  leafId: string;
}

/** Walk parentId from leaf to root, then reverse (spine only; no aside expansion). */
export function spineToRoot(messages: ChatMessage[], leafId: string | null | undefined): ChatMessage[] {
  if (!leafId) return [];
  const byId = new Map(messages.map((m) => [m.id, m]));
  const spine: ChatMessage[] = [];
  const seen = new Set<string>();
  let cur: string | null = leafId;
  while (cur) {
    if (seen.has(cur)) break;
    seen.add(cur);
    const m = byId.get(cur);
    if (!m) break;
    spine.push(m);
    cur = m.parentId ?? null;
  }
  spine.reverse();
  return spine;
}

/**
 * Active transcript: spine from leaf, with aside children of each assistant
 * inserted immediately after that assistant (ordered by seq).
 */
export function activePath(messages: ChatMessage[], leafId: string | null | undefined): ChatMessage[] {
  const spine = spineToRoot(messages, leafId);
  if (!spine.length) {
    // Legacy / empty leaf: fall back to seq order (linear).
    if (!leafId && messages.length) return [...messages].sort((a, b) => a.seq - b.seq);
    return [];
  }
  const asidesByParent = new Map<string, ChatMessage[]>();
  for (const m of messages) {
    if (m.role !== 'aside' || !m.parentId) continue;
    const list = asidesByParent.get(m.parentId) ?? [];
    list.push(m);
    asidesByParent.set(m.parentId, list);
  }
  for (const list of asidesByParent.values()) list.sort((a, b) => a.seq - b.seq);

  const out: ChatMessage[] = [];
  for (const m of spine) {
    if (m.role === 'aside') continue; // asides are expanded from assistant parents
    out.push(m);
    if (m.role === 'assistant') {
      const asides = asidesByParent.get(m.id) ?? [];
      out.push(...asides);
    }
  }
  return out;
}

/** Siblings of `id` (same parentId), including itself, ordered by seq. */
export function siblings(messages: ChatMessage[], id: string): ChatMessage[] {
  const self = messages.find((m) => m.id === id);
  if (!self) return [];
  const parent = self.parentId ?? null;
  return messages
    .filter((m) => (m.parentId ?? null) === parent && m.role === self.role)
    .sort((a, b) => a.seq - b.seq);
}

/** Group an active path into turn steps (user + optional assistant + asides). */
export function turnSteps(path: ChatMessage[]): TurnStep[] {
  const steps: TurnStep[] = [];
  let i = 0;
  while (i < path.length) {
    const m = path[i];
    if (m.role !== 'user') {
      // Orphan assistant/aside at start — treat as a degenerate step with a synthetic leaf.
      if (m.role === 'assistant') {
        const asides: ChatMessage[] = [];
        let j = i + 1;
        while (j < path.length && path[j].role === 'aside' && path[j].turnId === m.turnId) {
          asides.push(path[j]);
          j++;
        }
        steps.push({
          user: m,
          assistant: m,
          asides,
          leafId: asides.length ? asides[asides.length - 1].id : m.id,
        });
        i = j;
      } else {
        i++;
      }
      continue;
    }
    const user = m;
    i++;
    let assistant: ChatMessage | null = null;
    const asides: ChatMessage[] = [];
    if (i < path.length && path[i].role === 'assistant' && path[i].turnId === user.turnId) {
      assistant = path[i];
      i++;
      while (i < path.length && path[i].role === 'aside' && path[i].turnId === user.turnId) {
        asides.push(path[i]);
        i++;
      }
    } else if (i < path.length && path[i].role === 'assistant') {
      // Assistant without matching turnId still belongs to this user if next.
      assistant = path[i];
      i++;
      while (i < path.length && path[i].role === 'aside') {
        asides.push(path[i]);
        i++;
      }
    }
    const leafId = asides.length
      ? asides[asides.length - 1].id
      : assistant
        ? assistant.id
        : user.id;
    steps.push({ user, assistant, asides, leafId });
  }
  return steps;
}

/** Leaf id after removing `stepsBack` turn steps from the end of the path. Null if nothing left. */
export function leafAfterStepBack(path: ChatMessage[], stepsBack = 1): string | null {
  const steps = turnSteps(path);
  if (stepsBack <= 0) return path.length ? path[path.length - 1].id : null;
  if (stepsBack >= steps.length) return null;
  return steps[steps.length - 1 - stepsBack].leafId;
}

/** Whether the path has at least one turn that can be undone. */
export function canUndoTurn(path: ChatMessage[]): boolean {
  return turnSteps(path).length >= 1;
}

/**
 * Deepest descendant of `rootId`, preferring children that lie on `preferPathIds`
 * when choosing among siblings; otherwise highest seq.
 */
export function deepestDescendant(
  messages: ChatMessage[],
  rootId: string,
  preferPathIds?: Set<string>,
): string {
  const childrenOf = new Map<string, ChatMessage[]>();
  for (const m of messages) {
    if (!m.parentId || m.role === 'aside') continue;
    const list = childrenOf.get(m.parentId) ?? [];
    list.push(m);
    childrenOf.set(m.parentId, list);
  }
  for (const list of childrenOf.values()) list.sort((a, b) => a.seq - b.seq);

  let cur = rootId;
  for (;;) {
    const kids = childrenOf.get(cur);
    if (!kids?.length) return cur;
    const preferred = preferPathIds ? kids.find((k) => preferPathIds.has(k.id)) : undefined;
    cur = (preferred ?? kids[kids.length - 1]).id;
  }
}

/** 1-based index of message among its role-siblings, or null if alone. */
export function siblingIndex(messages: ChatMessage[], id: string): { index: number; total: number } | null {
  const sibs = siblings(messages, id);
  if (sibs.length <= 1) return null;
  const idx = sibs.findIndex((s) => s.id === id);
  if (idx < 0) return null;
  return { index: idx + 1, total: sibs.length };
}
